/** The lifecycle in order, with each stage's colour in the bar. */
const STAGES = [
  { id: 'Draft', label: 'Draft', bar: 'bg-background-400', dot: 'bg-background-400' },
  { id: 'Recruiting', label: 'Recruiting', bar: 'bg-secondary-400', dot: 'bg-secondary-400' },
  { id: 'Active', label: 'Active', bar: 'bg-accent-500', dot: 'bg-accent-500' },
  { id: 'Review', label: 'Review', bar: 'bg-primary-300', dot: 'bg-primary-300' },
  { id: 'Paying', label: 'Paying', bar: 'bg-primary-500', dot: 'bg-primary-500' },
  { id: 'Completed', label: 'Completed', bar: 'bg-primary-800', dot: 'bg-primary-800' },
] as const;

interface CampaignPipelineProps {
  /** Campaigns per status (from the list); archived ones are left out. */
  counts: Record<string, number>;
  /** The status filter in use ('all' or a status). */
  status: string;
  onPick: (status: string) => void;
}

/**
 * Where the agency's campaigns are in their lifecycle: one bar split by stage, growing in from the
 * left, with a legend underneath. Clicking a stage (bar or legend) filters the list to it;
 * clicking it again shows everything.
 */
export default function CampaignPipeline({ counts, status, onPick }: CampaignPipelineProps) {
  const stages = STAGES.map((stage) => ({ ...stage, count: counts[stage.id] ?? 0 }));
  const total = stages.reduce((sum, stage) => sum + stage.count, 0);
  if (total === 0) return null;
  const pick = (id: string) => onPick(status === id ? 'all' : id);

  return (
    <section className="section-lift rounded-lg border border-background-200 bg-background-50 px-5 py-4" aria-label="Campaign pipeline">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <h2 className="font-heading text-sm font-semibold text-foreground-950">Pipeline</h2>
        <p className="text-xs text-foreground-500">Where your {total} campaigns are · click a stage to filter</p>
      </div>
      <div className="mt-3 flex h-3 overflow-hidden rounded-full bg-background-200">
        {stages.map((stage, index) =>
          stage.count > 0 ? (
            <button
              key={stage.id}
              type="button"
              title={`${stage.label}: ${stage.count}`}
              aria-label={`Show ${stage.label} campaigns (${stage.count})`}
              onClick={() => pick(stage.id)}
              className={`share-grow h-full cursor-pointer transition-opacity duration-300 ${stage.bar} ${index > 0 ? 'border-l-2 border-background-50' : ''} ${
                status !== 'all' && status !== stage.id ? 'opacity-30' : 'hover:brightness-110'
              }`}
              style={{ width: `${(stage.count / total) * 100}%`, animationDelay: `${index * 90}ms` }}
            />
          ) : null,
        )}
      </div>
      <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1.5">
        {stages.map((stage) => (
          <button
            key={stage.id}
            type="button"
            disabled={stage.count === 0}
            onClick={() => pick(stage.id)}
            className={`inline-flex cursor-pointer items-center gap-1.5 rounded-full px-1.5 py-0.5 text-xs transition-colors disabled:cursor-default disabled:opacity-40 ${
              status === stage.id ? 'bg-primary-50 font-semibold text-primary-800' : 'text-foreground-600 hover:text-foreground-950'
            }`}
          >
            <span className={`h-2 w-2 rounded-full ${stage.dot}`} aria-hidden="true" />
            {stage.label}
            <span className="tabular-nums text-foreground-400">{stage.count}</span>
          </button>
        ))}
      </div>
    </section>
  );
}
